<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="stockClosingTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="stockClosingTitle">
            Manueller Abschluss {{ device?.stock_number ?? '' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>
        <div class="modal-body">
          <p v-if="device" class="closing-intro">
            {{ [device.master.description, device.color?.description ?? '', device.memory_size_label]
              .filter(part => part !== '').join(' · ') }}
          </p>

          <div v-if="loadError" class="alert alert-danger py-2">{{ loadError }}</div>

          <div class="closing-types" role="group" aria-label="Abschlussart">
            <button
              v-for="type in types"
              :key="type.id"
              type="button"
              class="btn closing-type-btn"
              :class="typeId === type.id ? 'btn-primary' : 'btn-outline-primary'"
              :aria-pressed="typeId === type.id"
              :disabled="busy"
              @click="typeId = type.id"
            >{{ type.description }}</button>
          </div>

          <!-- Lagereingang HS bucht wirklich: Artikel statt Wert, der Preis ist der
               Handyshop-Einkaufspreis des Artikels und nicht eingebbar. -->
          <template v-if="booksHsReceipt">
            <div class="d-flex align-items-end gap-2">
              <div class="flex-grow-1">
                <label class="form-label" for="stock-closing-article">Artikel</label>
                <select
                  id="stock-closing-article"
                  v-model="shopDeviceId"
                  class="form-select"
                  :disabled="busy || candidatesLoading || !candidates || candidates.devices.length === 0"
                >
                  <!-- Ohne Refurbished-Artikel gibt es keine Vorauswahl - dann bewusst wählen. -->
                  <option v-if="shopDeviceId === null" :value="null" disabled>
                    {{ candidates && candidates.devices.length > 0 ? 'Bitte Artikel wählen …' : '—' }}
                  </option>
                  <option v-for="article in candidates?.devices ?? []" :key="article.id" :value="article.id">
                    {{ article.condition }} · {{ article.label }}{{ article.is_eol ? ' · EOL' : '' }}{{ article.price_missing ? ' · kein Preis' : '' }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="busy || candidatesLoading"
                @click="loadCandidates"
              >{{ candidatesLoading ? 'Lädt …' : 'Aktualisieren' }}</button>
            </div>
            <p v-if="selectedArticle" class="closing-price">
              Handyshop-Einkaufspreis:
              <strong v-if="selectedArticle.hs_ek_price !== null">{{ formatEur(selectedArticle.hs_ek_price) }} netto</strong>
              <span v-else class="text-danger">nicht hinterlegt — bitte im Gerätemanager nachtragen und „Aktualisieren“.</span>
            </p>
            <div v-if="candidatesError" class="alert alert-danger py-2 mt-2 mb-0">{{ candidatesError }}</div>
            <div v-else-if="candidates?.blocked_reason" class="alert alert-warning py-2 mt-2 mb-0">{{ candidates.blocked_reason }}</div>
            <p class="closing-hs-info">
              Das Gerät wird bei <strong>Repaircenter</strong> eingebucht, ein Lieferschein an die
              <strong>HS Zentrale</strong> erstellt und auf deinem A4-Drucker gedruckt. Der Lieferschein
              muss in Preding abgeschlossen werden.
            </p>
          </template>
          <!-- Ankauf HS.cc bucht wirklich: Grading wählen, Preis aus dem Master vorgeschlagen
               und änderbar; ohne Ankaufspreise am Master wird er von Hand eingegeben. -->
          <template v-else-if="booksAnkaufInvoice">
            <div v-if="ankauf?.blocked_reason" class="alert alert-warning py-2 mb-2">{{ ankauf.blocked_reason }}</div>
            <label class="form-label" for="stock-closing-grading">Grading</label>
            <select
              id="stock-closing-grading"
              v-model="gradingId"
              class="form-select"
              :disabled="busy || ankaufLoading || !ankauf"
              @change="loadAnkaufPrice(false)"
            >
              <option :value="null" disabled>Bitte Grading wählen …</option>
              <option v-for="grading in ankauf?.gradings ?? []" :key="grading.id" :value="grading.id">{{ grading.grading }}</option>
            </select>
            <p class="closing-hint">Vorbelegt aus dem Gerätezustand — bitte prüfen. Der Zustand des Lagergeräts wird angepasst.</p>

            <label class="form-label mt-2" for="stock-closing-ankauf-price">Ankaufspreis (EUR)</label>
            <input
              id="stock-closing-ankauf-price"
              v-model="value"
              type="text"
              inputmode="decimal"
              class="form-control"
              placeholder="z. B. 50,00"
              :disabled="busy || ankaufLoading"
            />
            <p v-if="ankaufLoading" class="closing-hint">Preisvorschlag wird geladen …</p>
            <p v-else-if="ankauf && ankauf.suggested_price !== null" class="closing-hint">
              Vorschlag aus dem Masterartikel: <strong>{{ formatEur(ankauf.suggested_price) }}</strong>
            </p>
            <p v-else-if="ankauf" class="closing-hint text-danger">Kein Ankaufspreis am Master hinterlegt — bitte Preis eingeben.</p>
            <div v-if="ankaufError" class="alert alert-danger py-2 mt-2 mb-0">{{ ankaufError }}</div>
            <p class="closing-hs-info">
              Erstellt die <strong>Ankaufsrechnung an Assona</strong> (Repaircenter), bucht das Gerät als
              Ankaufsartikel ein und schickt es per Lieferschein an den <strong>Ankauf</strong>. Auf deinem
              Etikettendrucker kommt das Etikett mit der E-Nummer — aufs Gerät kleben.
            </p>
          </template>
          <template v-else>
            <label class="form-label" for="stock-closing-value">Wert netto (EUR, optional)</label>
            <input
              id="stock-closing-value"
              v-model="value"
              type="text"
              inputmode="decimal"
              class="form-control"
              placeholder="z. B. 180,00"
              :disabled="busy"
            />
          </template>

          <label class="form-label mt-3" for="stock-closing-note">
            Notiz{{ noteRequired ? ' (Pflicht)' : ' (optional)' }}
          </label>
          <textarea
            id="stock-closing-note"
            v-model="note"
            class="form-control"
            rows="3"
            maxlength="500"
            :disabled="busy"
          ></textarea>

          <!-- Ein vorbereiteter Versand kann nie mehr stattfinden — er geht mit
               dem Abschluss weg. Das gedruckte Etikett liegt aber noch irgendwo. -->
          <div v-if="device?.shipment?.is_active" class="alert alert-warning py-2 mt-3 mb-0">
            Der vorbereitete Versand an <strong>{{ device.shipment.servicepartner.description }}</strong>
            wird storniert — das Versandetikett bitte entsorgen.
          </div>

          <p class="closing-final">Der Abschluss ist endgültig und kann nicht rückgängig gemacht werden.</p>

          <div v-if="error" class="alert alert-danger py-2 mb-0">{{ error }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="busy">Abbrechen</button>
          <button type="button" class="btn btn-primary" :disabled="!canSubmit" @click="onSubmit">
            {{ busy ? (booksHsReceipt ? 'Bucht ein …' : booksAnkaufInvoice ? 'Kauft an …' : 'Schließt ab …') : 'Abschließen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { Modal } from 'bootstrap';
import {
  closeDevice,
  getAnkaufPrice,
  getHsReceiptCandidates,
  listClosingTypes,
  type StockDevice,
  type StockDeviceAnkaufPrice,
  type StockDeviceClosingResult,
  type StockDeviceClosingType,
  type StockDeviceHsReceiptArticle,
  type StockDeviceHsReceiptCandidates,
} from '../services/stockDevices';

/**
 * Manueller Abschluss eines Lagergeräts: Art wählen, optional Wert netto und
 * Notiz (bei „Sonstiges" Pflicht), abschließen. Reine Dokumentation in R2,
 * endgültig. Ausnahme „Lagereingang HS": statt eines Werts wird ein Artikel gewählt
 * (Refurbished vorausgewählt), der Server bucht bei Repaircenter ein und schickt das
 * Gerät per Lieferschein an die HS Zentrale. Ausnahme „Ankauf HS.cc": Grading und
 * Ankaufspreis (Vorschlag aus dem Master, änderbar) sind Pflicht, der Server legt die
 * Ankaufsrechnung an Assona an und druckt das E-Nummer-Etikett.
 *
 * Das Modal schickt selbst ab, damit eine Ablehnung des Servers hier stehen
 * bleibt und die Eingaben nicht verloren gehen. `open(device)` löst mit dem
 * Ergebnis auf — oder mit null, wenn abgebrochen. Aufgelöst wird erst im
 * `hidden`-Event, damit ein Folge-Modal keinen Backdrop übernimmt.
 */

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((result: StockDeviceClosingResult | null) => void) | null = null;
let   result: StockDeviceClosingResult | null = null;

const device    = ref<StockDevice | null>(null);
const types     = ref<StockDeviceClosingType[]>([]);
const typeId    = ref<number | null>(null);
const value     = ref('');
const note      = ref('');
const busy      = ref(false);
const error     = ref<string | null>(null);
const loadError = ref<string | null>(null);

const candidates        = ref<StockDeviceHsReceiptCandidates | null>(null);
const candidatesLoading = ref(false);
const candidatesError   = ref<string | null>(null);
const shopDeviceId      = ref<number | null>(null);

const ankauf        = ref<StockDeviceAnkaufPrice | null>(null);
const ankaufLoading = ref(false);
const ankaufError   = ref<string | null>(null);
const gradingId     = ref<number | null>(null);

const selectedType = computed<StockDeviceClosingType | undefined>(() =>
  types.value.find(t => t.id === typeId.value));

const noteRequired   = computed<boolean>(() => selectedType.value?.requires_note === true);
const booksHsReceipt = computed<boolean>(() => selectedType.value?.books_hs_receipt === true);
const booksAnkaufInvoice = computed<boolean>(() => selectedType.value?.books_ankauf_invoice === true);

const selectedArticle = computed<StockDeviceHsReceiptArticle | undefined>(() =>
  candidates.value?.devices.find(d => d.id === shopDeviceId.value));

const hsReceiptReady = computed<boolean>(() =>
  !candidatesLoading.value
  && candidates.value !== null
  && candidates.value.blocked_reason === null
  && selectedArticle.value !== undefined
  && selectedArticle.value.hs_ek_price !== null);

const ankaufReady = computed<boolean>(() =>
  !ankaufLoading.value
  && ankauf.value !== null
  && ankauf.value.blocked_reason === null
  && gradingId.value !== null
  && /^\d{1,8}([.,]\d{1,2})?$/.test(value.value.trim())
  && Number(value.value.trim().replace(',', '.')) > 0);

const canSubmit = computed<boolean>(() =>
  !busy.value
  && typeId.value !== null
  && (!noteRequired.value || note.value.trim() !== '')
  && (!booksHsReceipt.value || hsReceiptReady.value)
  && (!booksAnkaufInvoice.value || ankaufReady.value));

// Die Artikel werden erst geladen, wenn „Lagereingang HS" gewählt wird.
watch(booksHsReceipt, books => {
  if (books && candidates.value === null) void loadCandidates();
});

// Grading und Preisvorschlag erst bei „Ankauf HS.cc"; ein schon getippter Wert bleibt.
watch(booksAnkaufInvoice, books => {
  if (books && ankauf.value === null) void loadAnkaufPrice(true);
});

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(item: StockDevice): Promise<StockDeviceClosingResult | null> {
  device.value    = item;
  typeId.value    = null;
  value.value     = '';
  note.value      = '';
  busy.value      = false;
  error.value     = null;
  loadError.value = null;
  result          = null;
  candidates.value      = null;
  candidatesError.value = null;
  shopDeviceId.value    = null;
  ankauf.value      = null;
  ankaufError.value = null;
  gradingId.value   = null;
  void loadTypes();
  return new Promise(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

async function loadTypes(): Promise<void> {
  if (types.value.length) return;
  try {
    types.value = await listClosingTypes();
  } catch (e) {
    loadError.value = (e as Error).message;
  }
}

/** Lädt die Artikel (neu) — nach dem Anlegen eines Artikels über „Aktualisieren". */
async function loadCandidates(): Promise<void> {
  if (!device.value) return;
  candidatesLoading.value = true;
  candidatesError.value   = null;
  try {
    const loaded = await getHsReceiptCandidates(device.value.id);
    candidates.value = loaded;
    // Eine bewusst getroffene Wahl bleibt, solange es den Artikel noch gibt.
    if (!loaded.devices.some(d => d.id === shopDeviceId.value)) {
      shopDeviceId.value = loaded.preselected_device_id;
    }
  } catch (e) {
    candidatesError.value = (e as Error).message;
  } finally {
    candidatesLoading.value = false;
  }
}

/**
 * Lädt Grading-Auswahl und Preisvorschlag. Beim ersten Laden gilt das Grading aus dem
 * Gerätezustand; bei jedem Grading-Wechsel ersetzt der neue Vorschlag den Preis.
 */
async function loadAnkaufPrice(initial: boolean): Promise<void> {
  if (!device.value) return;
  ankaufLoading.value = true;
  ankaufError.value   = null;
  try {
    const loaded = await getAnkaufPrice(device.value.id, initial ? null : gradingId.value);
    ankauf.value = loaded;
    if (initial) gradingId.value = loaded.grading_id;
    if (!initial || value.value.trim() === '') {
      value.value = loaded.suggested_price !== null ? loaded.suggested_price.toFixed(2).replace('.', ',') : '';
    }
  } catch (e) {
    ankaufError.value = (e as Error).message;
  } finally {
    ankaufLoading.value = false;
  }
}

function formatEur(amount: number): string {
  return amount.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
}

async function onSubmit(): Promise<void> {
  if (!canSubmit.value || !device.value || typeId.value === null) return;
  busy.value  = true;
  error.value = null;
  try {
    result = await closeDevice(device.value.id, {
      closing_type_id: typeId.value,
      value_net_eur:   booksHsReceipt.value || value.value.trim() === '' ? null : value.value.trim(),
      note:            note.value.trim() === '' ? null : note.value.trim(),
      shop_device_id:  booksHsReceipt.value ? shopDeviceId.value : null,
      grading_id:      booksAnkaufInvoice.value ? gradingId.value : null,
    });
    modal?.hide();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

function onHidden(): void {
  resolver?.(result);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.closing-intro {
  margin-bottom: 0.9rem;
}

.closing-types {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.closing-type-btn {
  flex: 1 1 auto;
}

.closing-price {
  margin: 0.5rem 0 0;
}

.closing-hint {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
}

.closing-hs-info {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
}

.closing-final {
  margin: 0.9rem 0 0;
  font-size: 0.875rem;
  color: var(--bs-secondary-color, #6c757d);
}

.alert + .closing-final {
  margin-top: 0.75rem;
}
</style>
